import React, { useEffect, useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Calendar, MapPin, Clock, Search, LayoutGrid, Tent, Music, Smile, Users, Heart } from 'lucide-react';
import { motion } from 'framer-motion';
import axios from 'axios';

export default function Shows() {
  const navigate = useNavigate();
  const [shows, setShows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Filter state
  const [searchQuery, setSearchQuery] = useState('');
  const [activeCategory, setActiveCategory] = useState('All');

  useEffect(() => {
    window.scrollTo(0, 0);
    fetchShows();
    window.addEventListener('locationChanged', fetchShows);
    return () => window.removeEventListener('locationChanged', fetchShows);
  }, []);

  const fetchShows = async () => {
    try {
      setLoading(true);
      const dist_id = localStorage.getItem('selected_dist_id');
      const distQuery = dist_id ? `?dist_id=${dist_id}` : '';
      const res = await axios.get(`/api/fetch_product_list${distQuery}`, {
        headers: {
          'Client-ID': 'lokanatya_client',
          'Client-Secret': 'super_secret_key_123',
          'Accept': 'application/json'
        }
      });
      const data = res.data;
      if (data && data.status && data.data) {
        const safeData = Array.isArray(data.data) ? data.data : (typeof data.data === 'object' ? Object.values(data.data) : []);
        setShows(safeData);
      } else {
        setShows([]);
      }
    } catch (err) {
      console.error('Error fetching shows:', err);
      setError(err.message || 'Failed to load shows');
    } finally {
      setLoading(false);
    }
  };

  const safeShows = useMemo(() => {
    return Array.isArray(shows) ? shows : (typeof shows === 'object' && shows ? Object.values(shows) : []);
  }, [shows]);

  // Derive unique categories from shows
  const categoriesFilters = useMemo(() => {
    const cats = new Set(safeShows.map(s => s.cat_name).filter(Boolean));
    return ['All', ...Array.from(cats)];
  }, [safeShows]);

  // Filter shows based on search query and active category
  const filteredShows = useMemo(() => {
    return safeShows.filter(show => {
      const matchesSearch = show.jatra_name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        show.cat_name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        show.jatra_location?.toLowerCase().includes(searchQuery.toLowerCase());
      const matchesCategory = activeCategory === 'All' || show.cat_name === activeCategory;
      return matchesSearch && matchesCategory;
    });
  }, [safeShows, searchQuery, activeCategory]);

  // Category configuration for the new UI
  const getCategoryStyles = (catName) => {
    const styles = {
      'All': {
        icon: LayoutGrid, label: 'All Shows', subtext: 'BROWSE ALL',
        bg: 'bg-[#4B4B4B]', iconBg: 'bg-pink-500/20', iconColor: 'text-pink-400',
        bgImg: 'https://images.unsplash.com/photo-1514525253161-7a46d19cd819?q=80&w=400&auto=format&fit=crop'
      },
      'Jatra': {
        icon: Tent, label: 'Jatra', subtext: 'TRADITIONAL',
        bg: 'bg-[#28282A]', iconBg: 'bg-orange-500/10', iconColor: 'text-orange-500',
        bgImg: 'https://images.unsplash.com/photo-1605651202774-7d573fd3f12d?q=80&w=400&auto=format&fit=crop'
      },
      'Opera': {
        icon: Music, label: 'Opera', subtext: 'MODERN',
        bg: 'bg-[#181A2A]', iconBg: 'bg-blue-500/20', iconColor: 'text-blue-400',
        bgImg: 'https://images.unsplash.com/photo-1507676184212-d0c30a514b87?q=80&w=400&auto=format&fit=crop'
      },
      'Comedy': {
        icon: Smile, label: 'Comedy', subtext: 'FUN SHOWS',
        bg: 'bg-[#2D1F1F]', iconBg: 'bg-orange-500/10', iconColor: 'text-orange-400',
        bgImg: 'https://images.unsplash.com/photo-1585699324551-f6c309eedeca?q=80&w=400&auto=format&fit=crop'
      },
      'Family': {
        icon: Users, label: 'Family', subtext: 'FAMILY ENTERTAINMENT',
        bg: 'bg-[#1D2A20]', iconBg: 'bg-yellow-500/10', iconColor: 'text-yellow-500',
        bgImg: 'https://images.unsplash.com/photo-1511895426328-dc8714191300?q=80&w=400&auto=format&fit=crop'
      },
      'Drama': {
        icon: Heart, label: 'Drama', subtext: 'EMOTIONAL STORIES',
        bg: 'bg-[#2A1D20]', iconBg: 'bg-blue-500/10', iconColor: 'text-blue-500',
        bgImg: 'https://images.unsplash.com/photo-1460723237483-7a6dc9d0b212?q=80&w=400&auto=format&fit=crop'
      },
    };
    return styles[catName] || {
      icon: LayoutGrid, label: catName, subtext: 'SHOWS',
      bg: 'bg-[#2A2A2A]', iconBg: 'bg-gray-500/20', iconColor: 'text-gray-400',
      bgImg: 'https://images.unsplash.com/photo-1514525253161-7a46d19cd819?q=80&w=400&auto=format&fit=crop'
    };
  };

  if (loading) {
    return (
      <div className="bg-[#fcfcfc] min-h-screen pb-16 animate-pulse">
        {/* Header Skeleton */}
        <div className="bg-[#0c0515] pt-12 pb-24 px-4 sm:px-6 lg:px-8">
          <div className="max-w-7xl mx-auto">
            <div className="h-10 bg-gray-800 w-64 rounded mb-4"></div>
            <div className="h-4 bg-gray-800 w-96 rounded"></div>
          </div>
        </div>

        {/* Main Content Area Skeleton */}
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 -mt-10 relative z-10">
          <div className="bg-white rounded-[30px] p-6 md:p-8 shadow-sm border border-gray-100 min-h-[600px]">
            {/* Search Bar & Categories Skeleton */}
            <div className="flex flex-col md:flex-row justify-between items-center gap-6 mb-8">
              <div className="w-full md:w-[350px] h-12 bg-gray-100 rounded-2xl"></div>
              <div className="w-full overflow-x-auto pb-2 flex gap-3">
                {[1, 2, 3, 4, 5, 6].map(i => (
                  <div key={i} className="h-10 w-24 bg-gray-100 rounded-full shrink-0"></div>
                ))}
              </div>
            </div>

            {/* Grid Skeleton */}
            <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-5 gap-3 md:gap-5 mt-6">
              {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map(i => (
                <div key={i} className="bg-gray-50 rounded-2xl h-[280px] border border-gray-100"></div>
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return <div className="min-h-screen bg-[#f8fafc] flex items-center justify-center text-red-500 font-bold">Failed to load shows: {error}</div>;
  }

  return (
    <div className="bg-[#fcfcfc] min-h-screen pb-20 ">

      {/* Hero Banner with Odia Cultural Background */}
      <div className="relative w-full h-[360px] sm:h-[400px] md:h-[440px] bg-[#170634] overflow-hidden flex items-center shadow-lg border-b border-purple-900/30">
        {/* Background Image */}
        <div className="absolute inset-0 z-0 flex justify-end">
          <img
            src="images/shows_banner_bg.jpg"
            alt="Odia Cultural Arts"
            className="w-full h-full object-cover object-right-top md:object-right filter contrast-105 brightness-100"
          />
          {/* Smooth gradient transition from solid dark left to transparent right */}
          <div className="absolute inset-0 bg-gradient-to-r from-[#170634] via-[#170634]/90 to-transparent w-full md:w-1/2 z-10 pointer-events-none"></div>
          <div className="absolute inset-x-0 bottom-0 h-10 bg-gradient-to-t from-[#170634] to-transparent z-10 pointer-events-none"></div>
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full relative z-20 flex items-center justify-between">
          <div className="flex-1 text-white max-w-lg py-6">
            <span className="inline-flex items-center gap-1.5 bg-red-600/30 border border-red-500/40 text-red-300 font-extrabold text-[10px] sm:text-xs tracking-widest uppercase px-3.5 py-1 rounded-full mb-3 backdrop-blur-md">
              🎭 JATRA TICKETS & SHOWS
            </span>
            <h1 className="text-3xl sm:text-5xl md:text-6xl font-black text-white tracking-tight mb-3 drop-shadow-md">
              All Shows
            </h1>
            <p className="text-gray-200 text-xs sm:text-sm md:text-base font-medium max-w-md mb-6 leading-relaxed drop-shadow-sm">
              Discover and book the best jatra performances happening across Odisha.
            </p>

            {/* Sleek Glassmorphism Search Bar */}
            <div className="relative max-w-md">
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search shows, artists, or locations..."
                className="w-full bg-white/15 hover:bg-white/20 focus:bg-white/25 backdrop-blur-md border border-white/30 text-white rounded-full py-3 sm:py-3.5 pl-10 pr-10 text-xs sm:text-sm font-medium outline-none focus:ring-2 focus:ring-red-500/60 transition-all placeholder:text-gray-300 shadow-xl"
              />
              <div className="absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-300">
                <Search size={16} />
              </div>
              {searchQuery && (
                <button
                  onClick={() => setSearchQuery('')}
                  className="absolute right-3.5 top-1/2 -translate-y-1/2 text-gray-300 hover:text-white text-xs font-bold bg-white/20 hover:bg-white/30 w-5 h-5 rounded-full flex items-center justify-center transition-colors"
                >
                  ✕
                </button>
              )}
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10">

        {/* Notification Banner */}
        {/* <div className="bg-blue-500 rounded-2xl p-4 flex items-center justify-between mb-8 shadow-md">
          <div className="flex items-center gap-3 overflow-hidden">
            <div className="shrink-0 text-white">
              <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M11 18l-5-5H2v-2h4l5-5v12z"></path><path d="M15.54 8.46a5 5 0 0 1 0 7.07"></path><path d="M19.07 4.93a10 10 0 0 1 0 14.14"></path></svg>
            </div>
            <div className="flex flex-col min-w-0">
              <span className="text-white font-bold text-xs">Notification •</span>
              <span className="text-blue-100 text-xs truncate">Tickets for Opera Maheshwari are selling fast! Book now before they run out.</span>
            </div>
          </div>
          <div className="shrink-0 text-white font-bold text-sm ml-2">+1</div>
        </div> */}

        {/* Section Header */}
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <div className="w-1 h-4 bg-red-600 rounded-full"></div>
            <h2 className="text-sm font-black tracking-widest text-gray-900 uppercase">Coming Up</h2>
          </div>
          <button className="text-xs font-bold text-red-600 uppercase hover:text-red-700 transition">See All &rarr;</button>
        </div>

        {/* Filters (Pills) */}
        <div className="flex gap-3 overflow-x-auto pb-4 hide-scroll-bar w-full px-1 mb-2">
          {categoriesFilters.map((cat) => {
            const isActive = activeCategory === cat;
            return (
              <button
                key={cat}
                onClick={() => setActiveCategory(cat)}
                className={`shrink-0 px-5 py-1.5 rounded-full text-xs font-bold transition-all border ${isActive
                  ? 'border-red-600 text-red-600 bg-red-50'
                  : 'border-gray-200 text-gray-500 hover:border-gray-400'
                  }`}
              >
                {cat}
              </button>
            );
          })}
        </div>

        {filteredShows.length === 0 ? (
          <div className="text-center py-20 bg-white rounded-2xl shadow-sm border border-gray-100">
            <div className="text-5xl mb-4 opacity-50">🎭</div>
            <h3 className="text-lg font-bold text-gray-700">No shows available</h3>
            <p className="text-gray-400 mt-2">No shows match your current filters.</p>
          </div>
        ) : (
          <motion.div
            variants={{
              hidden: { opacity: 0 },
              show: { opacity: 1, transition: { staggerChildren: 0.1 } }
            }}
            initial="hidden"
            whileInView="show"
            viewport={{ once: true, amount: 0.1 }}
            className="grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-6"
          >
            {filteredShows.map((show, index) => {
              const dateObj = show.jatra_date ? new Date(show.jatra_date) : null;
              const formattedDate = dateObj
                ? dateObj.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })
                : '29 Jul';
              const imageUrl = show.image || "https://upload.wikimedia.org/wikipedia/commons/4/41/Placeholder_view_vector.svg";

              return (
                <motion.div
                  variants={{
                    hidden: { opacity: 0, y: 20 },
                    show: { opacity: 1, y: 0, transition: { duration: 0.4, ease: "easeOut" } }
                  }}
                  key={show.jatra_id || index}
                  className="group cursor-pointer bg-white rounded-2xl sm:rounded-3xl shadow-sm hover:shadow-xl hover:-translate-y-1 transition-all duration-300 border border-gray-100/80 overflow-hidden flex flex-col justify-between h-full relative"
                  onClick={() => navigate(`/shows/${show.jatra_id || show.product_id}`)}
                >
                  {/* Top Image Container */}
                  <div className="relative aspect-[4/3] sm:aspect-[4/3] w-full overflow-hidden bg-gray-100 shrink-0">
                    <img
                      src={imageUrl}
                      alt={show.jatra_name}
                      className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                      onError={(e) => { e.target.src = "https://upload.wikimedia.org/wikipedia/commons/4/41/Placeholder_view_vector.svg" }}
                    />
                    
                    {/* Top Left Category Pill */}
                    <div className="absolute top-2 left-2 sm:top-3 sm:left-3 z-10 bg-red-600 text-white text-[9px] sm:text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 sm:px-2.5 sm:py-1 rounded-md shadow-md max-w-[85px] sm:max-w-[130px] truncate">
                      {show.cat_name || 'Show'}
                    </div>

                    {/* Top Right Price Pill */}
                    <div className="absolute top-2 right-2 sm:top-3 sm:right-3 z-10 bg-black/75 backdrop-blur-md text-white text-[10px] sm:text-xs font-black px-2 py-0.5 sm:px-2.5 sm:py-1 rounded-md border border-white/20 shadow-md">
                      ₹{show.ticket_price || '200'}
                    </div>
                  </div>

                  {/* Card Content Body */}
                  <div className="p-2.5 sm:p-4 flex flex-col justify-between flex-1 bg-white">
                    <div>
                      {/* Show Name (100% visible, crisp text) */}
                      <h3 className="text-xs sm:text-base font-bold text-gray-900 leading-snug line-clamp-2 min-h-[2rem] sm:min-h-[2.5rem] mb-1.5 sm:mb-2 group-hover:text-red-600 transition-colors" title={show.jatra_name}>
                        {show.jatra_name}
                      </h3>

                      {/* Date & Location Rows */}
                      <div className="space-y-1 text-[10px] sm:text-xs font-medium text-gray-500">
                        <div className="flex items-center text-gray-600 truncate">
                          <Calendar size={12} className="mr-1.5 text-red-500 shrink-0 sm:w-3.5 sm:h-3.5" />
                          <span className="truncate">{formattedDate} • {show.jatra_time || '11:00 pm'}</span>
                        </div>
                        <div className="flex items-center text-gray-500 truncate">
                          <MapPin size={12} className="mr-1.5 text-red-400 shrink-0 sm:w-3.5 sm:h-3.5" />
                          <span className="truncate">{show.jatra_location || 'Odisha'}</span>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Full Width Bottom Button */}
                  <div className="bg-red-600 hover:bg-red-700 group-hover:bg-red-700 text-white font-bold py-2 sm:py-3 px-3 text-xs sm:text-sm flex items-center justify-center gap-1.5 sm:gap-2 transition-colors mt-auto shrink-0">
                    <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" className="sm:w-4 sm:h-4"><path d="M2 9a3 3 0 0 1 0 6v2a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2v-2a3 3 0 0 1 0-6V7a2 2 0 0 0-2-2H4a2 2 0 0 0-2 2Z"></path><path d="M13 5v2"></path><path d="M13 17v2"></path><path d="M13 11v2"></path></svg>
                    Book Now
                  </div>
                </motion.div>
              );
            })}
          </motion.div>
        )}
      </div>
    </div>
  );
}
